import React, { useEffect, useState } from "react";
import { apiFetch } from "../../../../apiFetch";
import { getFlagCode } from "../../../../utils/countryFlags";
import "./Leaderboard.css";

const Leaderboard = () => {
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Auto-current date defaults
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    // Filters / controls
    const [year, setYear] = useState(currentYear);
    const [mode, setMode] = useState("yearly");
    const [month, setMonth] = useState(currentMonth);

    // Admin can view both genders
    const [gender, setGender] = useState("male");

    const [position, setPosition] = useState(null);
    const [limit, setLimit] = useState(10);

    useEffect(() => {
        const fetchLeaderboard = async () => {
            setLoading(true);
            setError(null);

            try {
                const path =
                    mode === "monthly"
                        ? "/api/leaderboard/monthly"
                        : "/api/leaderboard";

                const params = new URLSearchParams({
                    year: String(year),
                    gender: String(gender),
                    limit: String(limit),
                });

                if (mode === "monthly") params.set("month", String(month));
                if (position) params.set("position", String(position));

                const res = await apiFetch(`${path}?${params.toString()}`);

                if (!res.ok) {
                    const errData = await res.json().catch(() => ({}));
                    throw new Error(errData?.message || "Failed to load leaderboard");
                }

                const data = await res.json();
                setPlayers(data?.top || []);
            } catch (err) {
                console.error("Admin leaderboard fetch failed:", err);
                setError("Failed to load leaderboard");
            } finally {
                setLoading(false);
            }
        };

        fetchLeaderboard();
    }, [year, mode, month, gender, position, limit]);

    return (
        <div className="leaderboard-page">
            <header className="leaderboard-header">
                <h1>Leaderboard</h1>
                <p>
                    {mode === "monthly"
                        ? "Top players for the selected month"
                        : "Top players based on yearly performance"}
                </p>

                <p className="leaderboard-explainer">
                    This leaderboard reflects the same eligibility and ranking model
                    used across the platform. Only players with sufficient verified
                    data are ranked.
                </p>
            </header>

            <section className="leaderboard-filters">
                <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
                    <option value={currentYear}>{currentYear}</option>
                    <option value={currentYear - 1}>{currentYear - 1}</option>
                </select>

                <select
                    value={mode}
                    onChange={(e) => {
                        const newMode = e.target.value;
                        setMode(newMode);
                        if (newMode === "monthly") {
                            setMonth(currentMonth);
                        }
                    }}
                >
                    <option value="yearly">Yearly</option>
                    <option value="monthly">Monthly</option>
                </select>

                {mode === "monthly" && (
                    <select
                        value={month}
                        onChange={(e) => setMonth(Number(e.target.value))}
                    >
                        <option value={1}>January</option>
                        <option value={2}>February</option>
                        <option value={3}>March</option>
                        <option value={4}>April</option>
                        <option value={5}>May</option>
                        <option value={6}>June</option>
                        <option value={7}>July</option>
                        <option value={8}>August</option>
                        <option value={9}>September</option>
                        <option value={10}>October</option>
                        <option value={11}>November</option>
                        <option value={12}>December</option>
                    </select>
                )}

                <select value={gender} onChange={(e) => setGender(e.target.value)}>
                    <option value="male">Men</option>
                    <option value="female">Women</option>
                </select>

                <select
                    value={position ?? ""}
                    onChange={(e) => setPosition(e.target.value || null)}
                >
                    <option value="">All Positions</option>
                    <option value="ST">ST</option>
                    <option value="CF">CF</option>
                    <option value="LW">LW</option>
                    <option value="RW">RW</option>
                    <option value="AM">AM</option>
                    <option value="CM">CM</option>
                    <option value="CDM">CDM</option>
                    <option value="CB">CB</option>
                    <option value="LB">LB</option>
                    <option value="RB">RB</option>
                    <option value="GK">GK</option>
                </select>

                <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
                    <option value={5}>Top 5</option>
                    <option value={10}>Top 10</option>
                    <option value={20}>Top 20</option>
                </select>
            </section>

            <section className="leaderboard-shell">
                <section className="leaderboard-list">
                    {loading && (
                        <div className="leaderboard-message">
                            Loading leaderboard…
                        </div>
                    )}

                    {!loading && error && (
                        <div className="leaderboard-error">{error}</div>
                    )}

                    {!loading && !error && players.length === 0 && (
                        <div className="leaderboard-message">
                            No eligible players for this period.
                        </div>
                    )}

                    {!loading &&
                        !error &&
                        players.map((player, index) => {
                            const flagCode = getFlagCode(player.nationality);
                            const isTop = index === 0;

                            const rawDelta = player.movementDelta;
                            const delta =
                                rawDelta === null || rawDelta === undefined
                                    ? null
                                    : Number(rawDelta);

                            const movementClass =
                                delta > 0 ? "up" : delta < 0 ? "down" : "neutral";

                            const displayName =
                                player.fullName?.trim() || player.username || "Unknown Player";

                            return (
                                <div
                                    key={player.playerId || player.username || index}
                                    className={`leaderboard-row ${isTop ? "highlight" : ""}`}
                                >
                                    <div className="rank">#{index + 1}</div>

                                    <div className="player-info">
                                        <div className="player-line">
                                            {flagCode && (
                                                <img
                                                    src={`/flags/${flagCode}.svg`}
                                                    alt={player.nationality}
                                                    className="flag"
                                                />
                                            )}

                                            <div className="player-name-wrap">
                                                <span className="player-name">
                                                    {displayName}
                                                </span>

                                                {player.username && (
                                                    <span className="player-username">
                                                        @{player.username}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="player-meta">
                                            {player.position} · {player.nationality}
                                        </div>
                                    </div>

                                    <div className={`movement ${movementClass}`}>
                                        {delta === null ? (
                                            <span className="delta neutral">NEW</span>
                                        ) : delta === 0 ? (
                                            <span className="delta neutral">0</span>
                                        ) : (
                                            <>
                                                <span className="arrow">
                                                    {delta > 0 ? "↑" : "↓"}
                                                </span>
                                                <span className="delta">
                                                    {delta > 0 ? `+${delta}` : delta}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                </section>
            </section>
        </div>
    );
};

export default Leaderboard;




